import React, { useEffect, useRef, useState } from 'react';

import { FormikField as Field, Input, PancakeStack, Text, VStack, WorkflowHeader } from '@betterment/samba-react';
import { ErrorBoundary } from '@betterment/shared-components';
import { useFallbackKeys } from '@betterment/shared-components/src/hooks/useFallbackKeys';
import { Form, Formik } from 'formik';

import { AgentsDropdown } from './components/AgentsDropdown';
import { ChatMessage, Message, MessageDirection } from './components/Message';
import { useSendMessage } from './hooks/useSendMessage';
import { useSessionState } from './hooks/useSessionState';

import { WithLayout } from '../RetailApp/components/WithLayout';

export const AgentsPlaygroundPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { sessionState } = useSessionState();

  const [sendMessage] = useSendMessage();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  useEffect(() => {
    document.body.classList.add('HideChatbot');
    return () => document.body.classList.remove('HideChatbot');
  }, []);

  useEffect(() => setMessages([]), [sessionState.sessionId]);

  const messagesWithKeys = useFallbackKeys(messages);

  return (
    <ErrorBoundary fallbackType="full" feature="agents playground" owner="agents">
      <WithLayout parts={{ sidebar: false, footer: false }} feature="agents_playground">
        <PancakeStack css={{ maxWidth: '100%', height: '100vh' }}>
          <PancakeStack.Header
            css={{
              maxWidth: '100%',
              background: 'background.surface',
              borderBottom: '1px solid token(colors.border.weak)',
            }}
          >
            <WorkflowHeader css={{ padding: 'space.0', justifySelf: 'center' }}>
              <WorkflowHeader.Leading />
              <WorkflowHeader.Title>
                <VStack css={{ gap: 'space.50', alignItems: 'center' }}>
                  <Text>Agents playground</Text>
                  <Text variant="caption" css={{ color: 'text.subtle' }}>
                    Agent: {sessionState.agentConfig} | Session: {sessionState.sessionId}
                  </Text>
                </VStack>
              </WorkflowHeader.Title>
              <WorkflowHeader.Trailing>
                <AgentsDropdown />
              </WorkflowHeader.Trailing>
            </WorkflowHeader>
          </PancakeStack.Header>
          <PancakeStack.Main
            css={{
              padding: 'space.300',
              background: 'background.surface',
              display: 'flex',
              gap: 'space.300',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <VStack css={{ flex: 1, overflowY: 'auto', gap: 'space.200' }}>
              {messagesWithKeys.map(({ item, key }) => (
                <Message key={key} {...item} />
              ))}
              <div ref={messagesEndRef} />
            </VStack>
            <VStack css={{ flexShrink: 0 }}>
              <Formik
                initialValues={{ inputText: '' }}
                onSubmit={async ({ inputText }, { resetForm }) => {
                  if (inputText.trim()) {
                    const userMessage = { content: inputText, direction: MessageDirection.Outgoing };
                    setMessages([...messages, userMessage]);
                    const result = await sendMessage({ inputText, ...sessionState });
                    if (result?.data?.message) {
                      const responseMessage = { content: result.data.message, direction: MessageDirection.Incoming };
                      setMessages([...messages, userMessage, responseMessage]);
                      resetForm();
                    } else {
                      setMessages(messages);
                    }
                  }
                }}
              >
                <Form>
                  <Field name="inputText">
                    <Field.Control>
                      <Input
                        alignment="left"
                        autoFocus={true}
                        aria-label="Input text"
                        placeholder="Type a message..."
                      />
                    </Field.Control>
                  </Field>
                </Form>
              </Formik>
            </VStack>
          </PancakeStack.Main>
        </PancakeStack>
      </WithLayout>
    </ErrorBoundary>
  );
};
